import {WizardOptionType, WizardValueType} from '@optimaxdev/wizard-ms';
import {
    any,
    both,
    compose,
    equals,
    includes,
    pathOr,
    pipe,
    propEq,
    propOr,
    reduceBy,
    sortBy,
    toPairs,
} from 'ramda';
import {createSelector} from 'reselect';

import {ApplicationStoreType} from 'reducers';
import {isSunglassesParentProduct} from 'selectors/product/product';

import {
    BIFOCAL,
    DISTANCE,
    DISTANCE_AND_READING,
    DISTANCE_AND_READING_DESCRIPTION,
    NOPRESCRIPTION,
    OFFICE_NEAR,
    PROGRESSIVES,
} from '../../constants/usages';
import {getCustomerConfiguration} from '../../reducers/customerConfiguration/customerConfiguration';
import {GroupedUsageCollectionType, UsageItemType} from '../../reducers/usages/usages.types';

/**
 * Provides the usage node
 * @param {ApplicationStoreType} store - store
 * @returns {WizardOptionType} node
 */
export const getUsageNode = (store: ApplicationStoreType): WizardOptionType =>
    store.wizardMs.usages;

/**
 * Provides the usage items
 * @param {ApplicationStoreType} store - store
 * @returns {WizardValueType[]} node items
 */
export const getUsagesItems = (store: ApplicationStoreType): WizardValueType[] =>
    store.wizardMs.usages.items || [];

/**
 * Provides selected usage node
 */
export const getActiveUsage = createSelector(
    getUsageNode,
    getCustomerConfiguration,
    (usageNode, configuration): WizardValueType | {} =>
        (usageNode.items || []).find(
            usageItem => usageItem.node === configuration.options[usageNode.node],
        ) || {},
);

/**
 * Selector for experiment progressive funnel
 */
export const getActiveUsages = createSelector(getActiveUsage, (selectedUsage): {
    isMultifocalSelected: boolean;
    isProgressiveSelected: boolean;
} => ({
    isMultifocalSelected:
        (selectedUsage as UsageItemType)?.sku === PROGRESSIVES ||
        (selectedUsage as UsageItemType)?.sku === BIFOCAL,
    isProgressiveSelected: (selectedUsage as UsageItemType)?.sku === PROGRESSIVES,
}));

/**
 * Get active usage sku
 */
export const getActiveUsageSku = createSelector(
    getActiveUsage,
    (value: WizardValueType | {}): string => ('sku' in value ? value.sku : ''),
);

/**
 * if active usage is single vision it returns boolean true/false
 */
export const isUsageSingleVision = createSelector(
    getActiveUsageSku,
    activeUsageSku => activeUsageSku === DISTANCE,
);

/**
 * Checks if selected usage is non prescription
 */
export const isFashionUsage = createSelector(
    getActiveUsage,
    pipe(propOr('', 'sku'), equals(NOPRESCRIPTION)),
);

/** ******* functions and selectors only for ui ************ */

/**
 * Accumulates the Usage items PROGRESSIVES, BIFOCAL to a single value.
 * @param {Array<string, WizardValueType>} accumulator - Accumulated value.
 * @param {WizardValueType} value - Usage item
 * @returns {GroupedUsageCollectionType} Accumulated usage collection
 */
export const accumulateUsage = (
    accumulator: GroupedUsageCollectionType | WizardValueType | {values: []},
    value: WizardValueType,
): GroupedUsageCollectionType | WizardValueType =>
    includes(value.sku, [PROGRESSIVES, BIFOCAL])
        ? {
              ...DISTANCE_AND_READING_DESCRIPTION,
              parentNodeId: Number(value.parentNodeId),
              values: (accumulator as GroupedUsageCollectionType).values
                  ? (accumulator as GroupedUsageCollectionType).values.concat(
                        value as UsageItemType,
                    )
                  : [],
              sort: 'sort' in accumulator ? Math.min(accumulator.sort, value.sort) : value.sort,
              usageName: 'Multifocal',
              title: 'Distance & Reading',
          }
        : value;

/**
 * Groups the PROGRESSIVES and BIFOCAL to under one DISTANCE_AND_READING property.
 */
export const groupedUsages = createSelector(
    getUsagesItems,
    pipe(
        reduceBy<WizardValueType, WizardValueType | GroupedUsageCollectionType | {values: []}>(
            accumulateUsage,
            {values: []},
        )((
            value, // TODO - GUSA-9265 S.Kokalin seems it should be used as the third argument for reduceBy as keyFn
        ) =>
            includes(value.sku, [PROGRESSIVES, BIFOCAL]) ? DISTANCE_AND_READING : `${value.node}`,
        ),
        toPairs,
        sortBy(pathOr('sort', [1, 'sort'])),
    ),
);

/**
 * Is any usage option selected?
 */
export const isUsageSelected = createSelector(getActiveUsageSku, activeUsageSku =>
    Boolean(activeUsageSku),
);

/**
 * Get multifocal usages
 */
export const getMultifocalUsages = createSelector(getUsagesItems, usageItems =>
    usageItems.filter(usage => [PROGRESSIVES, BIFOCAL].includes(usage.sku)),
);

/**
 * Get progressive usages
 */
export const getProgressiveUsages = createSelector(getUsagesItems, items => {
    return items.filter(item => [OFFICE_NEAR, PROGRESSIVES].includes(item.sku));
});

/**
 * Checks if Office Lenses usage exists in the wizard data
 */
export const getIsOfficeLensesUsageExist = compose(
    any(both(propEq('sku', OFFICE_NEAR), propEq('enabled', true))),
    getUsagesItems,
);

/**
 * Checks if usages sub step should be visible
 * @param {ApplicationStoreType} store store
 * @returns {boolean} bool
 */
export const getIsUsagesSubStepInSteps = (store: ApplicationStoreType) =>
    !isSunglassesParentProduct(store) && getIsOfficeLensesUsageExist(store);
